import React, { useMemo, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import MaterialReactTable, {
  type MRT_ColumnDef,
} from "material-react-table";
import { Box, Modal, Typography, Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useQuery } from '@tanstack/react-query';
import { fetchFileList } from '../apiClient/fetchFileList';
import { DatasetProps } from '../pages/WorkspacePage';

// define type for data table
interface DataTableProps {
  setSelectedRows: Function;
  searchQuery?: string;
  confirmedStartDate?: Date | null;
  confirmedEndDate?: Date | null;
  northWest?: string;
  southEast?: string;
  geoPolygon?: string;
  bandName?: string[];
  tagName?: string[];
  campaignName?: string[];
  campaignId?: string;
  // show or hide MRT built-in selection (checkbox)
  enableRowSelection?: boolean;
  // show or hide custom SELECT/DESELECT ALL buttons
  enableCustomSelectAll?: boolean;
}

// define ref interface
export interface DataTableRef {
  resetAllSelectedIds: () => void;
}

// use forwardRef to expose resetAllSelectedIds function
export const DataTable = forwardRef<DataTableRef, DataTableProps>(
  (props, ref) => {
    const {
      setSelectedRows,
      searchQuery,
      confirmedStartDate,
      confirmedEndDate,
      northWest,
      southEast,
      geoPolygon,
      tagName,
      campaignName,
      campaignId,
      bandName,
      enableRowSelection = true,
      enableCustomSelectAll = true,
    } = props;

    // notification snackbar
    const { enqueueSnackbar } = useSnackbar();

    // Add state for pagination
    const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: 25, // updated from 10 to 25 to match backend default
    });

    // Add state for next/previous URLs
    const [pageUrls, setPageUrls] = useState<{
      next: string | null;
      previous: string | null;
    }>({ next: null, previous: null });

    // set totalCount from response, not from data: apiResponse - seems like MRT bug
    const [totalCount, setTotalCount] = useState(0);

    // row selection
    const [selectedCampaign, setSelectedCampaign] =
      useState<DatasetProps | null>(null);

    // add state for all selected IDs across all pages
    const [allSelectedIds, setAllSelectedIds] = useState<
      Record<string, boolean>
    >({});

    // Add state to track if all items are currently selected
    const [isAllSelected, setIsAllSelected] = useState(false);

    // Add state to track the count of all available items
    const [allItemsCount, setAllItemsCount] = useState(0);

    const handleRowClick = (row: DatasetProps) => {
      setSelectedCampaign(row);
    };

    const handleCloseModal = () => {
      setSelectedCampaign(null);
    };

    // Same filters as the table query — keep Select All in sync with the filtered result set
    const fileListFilterParams = useMemo(
      () => ({
        search: searchQuery,
        startDate: confirmedStartDate
          ? confirmedStartDate.toISOString()
          : undefined,
        endDate: confirmedEndDate
          ? confirmedEndDate.toISOString()
          : undefined,
        northWest,
        southEast,
        geoPolygon,
        tagName,
        campaignName,
        campaignId,
        bandName,
      }),
      [
        searchQuery,
        confirmedStartDate,
        confirmedEndDate,
        northWest,
        southEast,
        geoPolygon,
        tagName,
        campaignName,
        campaignId,
        bandName,
      ],
    );

    // set queryKey
    const queryKey = useMemo(
      () => [
        'sigcap_datatable',
        pagination.pageSize,
        pagination.pageIndex,
        searchQuery,
        campaignId,
      ],
      [pagination.pageSize, pagination.pageIndex, searchQuery, campaignId],
    );

    // Update the query to properly handle pagination: define isFetching too
    const {
      data: apiResponse = { results: [], count: 0, next: null, previous: null },
      isLoading: isLoadingTodos,
      isFetching: isFetchingTodos,
      isError,
    } = useQuery({
      queryKey: queryKey,
      queryFn: async () => {
        const response = await fetchFileList({
          ...fileListFilterParams,
          pageSize: pagination.pageSize,
          page: pagination.pageIndex + 1,
        });

        setPageUrls({
          next: response?.next || null,
          previous: response?.previous || null,
        });

        // set total number of data
        setTotalCount(response?.count || 0);

        return response;
      },
    });

    if (isError) {
      enqueueSnackbar('Found error(s)', {
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
      });
    }

    // set state to get selected row
    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>(
      {},
    );

    // Helper function to check if all items are selected
    const checkIfAllSelected = useMemo(() => {
      const selectedCount = Object.keys(rowSelection).length;
      return selectedCount > 0 && selectedCount === totalCount;
    }, [rowSelection, totalCount]);

    // Update isAllSelected when rowSelection or totalCount changes
    useEffect(() => {
      setIsAllSelected(checkIfAllSelected);
    }, [checkIfAllSelected]);

    // Update allItemsCount when totalCount changes
    useEffect(() => {
      setAllItemsCount(totalCount);
    }, [totalCount]);

    // column definitions
    const columns = useMemo<MRT_ColumnDef<DatasetProps>[]>(
      () => [
        {
          accessorKey: 'id',
          header: 'ID',
          // control individual column filter
          enableColumnFilter: false,
        },
        {
          accessorKey: 'name',
          header: 'File name',
          enableColumnFilter: false,
          size: 500,
          Cell: ({ cell }) => (
            <Box
              sx={{
                wordBreak: 'break-word',
                whiteSpace: 'normal',
              }}
            >
              {cell.getValue<string>()}
            </Box>
          ),
        },
        {
          accessorKey: 'num_captures',
          header: 'Number of Captures',
          enableColumnFilter: false,
          size: 150,
          Cell: ({ cell }) => {
            const value = cell.getValue<number>();
            return value !== undefined ? value.toLocaleString() : '0';
          },
        },
        {
          //convert to date for sorting and filtering
          accessorFn: (originalRow) => new Date(originalRow.earliest_capture),
          accessorKey: 'date',
          header: 'Start Date',
          size: 150,
          filterFn: 'betweenDate',
          // convert back to string for display
          Cell: ({ cell }) => cell.getValue<Date>().toISOString().split('T')[0],
        },
      ],
      [],
    );

    // update selected rows at parent component, WorkspacePage
    useEffect(() => {
      setSelectedRows(Object.keys(rowSelection));
    }, [rowSelection, setSelectedRows]);

    // useImperativeHandle to expose resetAllSelectedIds function
    useImperativeHandle(ref, () => ({
      resetAllSelectedIds: () => {
        setRowSelection({});
        setAllSelectedIds({});
        setIsAllSelected(false);
      },
    }));

    return (
      <>
        <MaterialReactTable
          data={apiResponse?.results || []}
          columns={columns}
          initialState={{
            showGlobalFilter: false,
            density: 'comfortable',
            columnVisibility: { id: false },
          }}
          enablePagination={true}
          manualPagination={true}
          enableRowSelection={enableRowSelection}
          selectAllMode="page"
          enableColumnFilters={false}
          enableHiding={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableSorting={false}
          enableColumnActions={false}
          enableGlobalFilter={false}
          // hide MRT message for selection
          positionToolbarAlertBanner={'none'}
          muiTableContainerProps={{
            sx: {
              overflowY: 'unset',
            },
          }}
          muiTablePaperProps={{
            sx: {
              display: 'flex',
              flexDirection: 'column',
              m: 0,
              borderRadius: 0,
              position: 'relative',
            },
          }}
          layoutMode="grid"
          getRowId={(row) => row.short_id}
          onRowSelectionChange={setRowSelection}
          state={{
            rowSelection,
            // also add isFetching for displaying loading
            isLoading: isLoadingTodos || isFetchingTodos,
            pagination,
          }}
          onPaginationChange={setPagination}
          muiTablePaginationProps={{
            rowsPerPageOptions: [25, 50, 100], // updated options to be more appropriate
            disabled: isLoadingTodos || isFetchingTodos,
            getItemAriaLabel: (
              type: 'next' | 'previous' | 'first' | 'last',
            ) => {
              if (type === 'next') {
                return pageUrls.next ? 'Go to next page' : 'No next page';
              }
              if (type === 'previous') {
                return pageUrls.previous
                  ? 'Go to previous page'
                  : 'No previous page';
              }
              return type;
            },
            showFirstButton: false,
            showLastButton: false,
          }}
          rowCount={totalCount}
          muiTableBodyRowProps={({ row }) => ({
            // Attach click handler to each row
            onClick: () => {
              handleRowClick(row.original);
            },
            sx: {
              // Add pointer cursor for better UX
              cursor: 'pointer',
              // add striping
              '&:nth-of-type(odd)': {
                backgroundColor: '#f5f5f5',
              },
              // override striping if selected
              ...(row.getIsSelected() && {
                backgroundColor: 'rgba(58, 111, 222, 0.2) !important',
              }),
            },
          })}
          // Conditionally render the entire top toolbar to eliminate white space
          renderTopToolbar={({ table }) =>
            enableCustomSelectAll ? (
              <Box
                sx={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center',
                  p: '0.5rem',
                }}
              >
                <Button
                  color="primary"
                  disabled={isAllSelected || totalCount === 0}
                  onClick={async () => {
                    try {
                      // Fetch all IDs if not already fetched or if data might have changed
                      if (
                        Object.keys(allSelectedIds).length === 0 ||
                        allItemsCount !== totalCount
                      ) {
                        const response = await fetchFileList({
                          ...fileListFilterParams,
                          selectAll: true,
                        });

                        const allIds = response.ids.reduce(
                          (acc: Record<string, boolean>, id: string) => {
                            acc[id] = true;
                            return acc;
                          },
                          {} as Record<string, boolean>,
                        );

                        setAllSelectedIds(allIds);
                        setRowSelection(allIds);
                        setAllItemsCount(response.count);

                        enqueueSnackbar(
                          `All ${response.count} items selected`,
                          {
                            variant: 'info',
                            anchorOrigin: {
                              vertical: 'top',
                              horizontal: 'center',
                            },
                          },
                        );
                      } else {
                        // Use already fetched IDs
                        setRowSelection(allSelectedIds);
                        enqueueSnackbar(`All ${totalCount} items selected`, {
                          variant: 'info',
                          anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'center',
                          },
                        });
                      }
                    } catch (error) {
                      enqueueSnackbar('Failed to select all items', {
                        variant: 'error',
                        anchorOrigin: {
                          vertical: 'top',
                          horizontal: 'center',
                        },
                      });
                    }
                  }}
                  variant="contained"
                  style={{
                    marginLeft: 'auto',
                    marginRight: '8px',
                  }}
                >
                  Select All
                </Button>
                <Button
                  color="secondary"
                  disabled={Object.keys(rowSelection).length === 0}
                  onClick={() => {
                    setRowSelection({});
                    // clear all selected IDs
                    setAllSelectedIds({});
                    setIsAllSelected(false);
                    enqueueSnackbar('All selections cleared', {
                      variant: 'info',
                      anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'center',
                      },
                    });
                  }}
                  variant="contained"
                >
                  Deselect All
                </Button>
              </Box>
            ) : null
          }
        />
        {/* Modal for table row details */}
        <Modal open={!!selectedCampaign} onClose={handleCloseModal}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'auto',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
            }}
          >
            {selectedCampaign && (
              <>
                <Typography variant="h6" gutterBottom>
                  <Box component="b">Campaign Details</Box>
                </Typography>
                {/* <Typography sx={{ marginTop: '0.5em' }}> */}
                <Typography
                  sx={{
                    marginTop: '0.5em',
                    whiteSpace: 'normal',
                    wordBreak: 'break-all',
                  }}
                >
                  <strong>File Name:</strong> {selectedCampaign.name}
                </Typography>
                <Typography sx={{ marginTop: '0.5em' }}>
                  {/* <strong>Number of Captures:</strong> {selectedCampaign.count} */}
                  <strong>Number of Captures:</strong>{' '}
                  {selectedCampaign.num_captures}
                </Typography>
                <Typography sx={{ marginTop: '0.5em' }}>
                  <strong>Start Date:</strong>{' '}
                  {
                    new Date(selectedCampaign.earliest_capture)
                      .toISOString()
                      .split('T')[0]
                  }
                </Typography>
                <Typography sx={{ marginTop: '0.5em' }}>
                  <strong>End Date:</strong>{' '}
                  {
                    new Date(selectedCampaign.latest_capture)
                      .toISOString()
                      .split('T')[0]
                  }
                </Typography>
                {selectedCampaign.campaign && (
                  <Typography sx={{ marginTop: '0.5em' }}>
                    <strong>Campaign:</strong> {selectedCampaign.campaign}
                  </Typography>
                )}
                {selectedCampaign.tags && selectedCampaign.tags.length > 0 && (
                  <Typography sx={{ marginTop: '1em' }}>
                    <strong>Tags:</strong>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        // Adds spacing between tags
                        gap: 1,
                        marginTop: '0.5em',
                      }}
                    >
                      {selectedCampaign.tags.map(
                        (tag: string, index: number) => (
                          <Box
                            key={index}
                            sx={{
                              // white background
                              backgroundColor: '#f0f0f0',
                              padding: '0.3em 0.6em',
                              borderRadius: '4px',
                              fontSize: '0.875em',
                            }}
                          >
                            {tag}
                          </Box>
                        ),
                      )}
                    </Box>
                  </Typography>
                )}
                {selectedCampaign.publications &&
                  selectedCampaign.publications.filter(
                    (pub) => pub.title !== null && pub.title !== '',
                  ).length > 0 && (
                    <Typography sx={{ marginTop: '1em' }}>
                      <strong>Publications:</strong>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          marginTop: '0.5em',
                        }}
                      >
                        {selectedCampaign.publications
                          .filter(
                            (pub) => pub.title !== null && pub.title !== '',
                          )
                          .map((publication, index: number) => (
                            <Box
                              key={index}
                              sx={{
                                backgroundColor: '#e3f2fd',
                                padding: '0.5em 0.8em',
                                borderRadius: '4px',
                                fontSize: '0.875em',
                                borderLeft: '3px solid #2196f3',
                              }}
                            >
                              {publication.title}
                            </Box>
                          ))}
                      </Box>
                    </Typography>
                  )}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    // Adds spacing between buttons
                    gap: 2,
                    mt: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCloseModal}
                  >
                    Close
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Modal>
      </>
    );
  },
);
